import {
  BaseLoader,
  BigInteger,
  Constraint,
  distinct,
  distinctmaybe,
  Integer,
  It,
  Json,
  many,
  maybe,
  one,
  or,
  Wrapped,
} from './baseLoader.js';

export const baseSchema = (baseLoader: BaseLoader): any => {
  const { Access, AccessProperty, DependsOn } = baseLoader;

  const Status = [
    // status of projects, members, and publications
    'concept', // concepts are not publicly visible
    'active', // active items are ongoing
    'finished', // finished items are no longer active
    'published', // for published publications
    'archived', // archived items are no longer publicly visible
    'former', // former members are shown as former
  ] as const;

  const rolesInheritance = {
    user: {
      // basic level of users
      inherits: [],
    },
    manager: {
      // managers are users who can moderate any timeline
      inherits: ['user'],
    },
    admin: {
      // admins have full access
      inherits: ['manager'],
    },
  };

  const Roles = Object.keys(rolesInheritance);

  // a File is considered to be immutable
  @Access((o: File) => ({
    create: 'user',
    read: '*',
    update: or(o.author, 'manager'),
    delete: or(o.author, 'manager'),
  }))
  class File extends It {
    description = one(String);
    @AccessProperty({
      read: '*',
      update: 'manager',
    })
    file_path = distinct(String);
    fileName = one(String);
    @AccessProperty({
      createUpdate: '',
    })
    author = one(User);
    uri = distinct(String);
    @AccessProperty({ read: '' })
    storage = one(String);
    size = one(Integer);
    mimeType = one(String);
    uploadTime = maybe(Date);

    folder: Wrapped<Folder> = maybe(Folder, (o) => o.files);

    sectionFiles: Wrapped<Section> = many(Section, (o) => o.files);
    avatarsFor = many(User);
    projectImageFor = many(Project);
    publicationImageFor = many(Publication);
    memberImageFor = many(Member);
  }

  @Access((o: User) => ({
    create: or('admin'),
    read: or(o.id, 'manager'),
    update: or(o.id, 'admin'),
    delete: or('admin'),
  }))
  class User extends It {
    @AccessProperty((o: User) => ({
      read: '*',
    }))
    username = one(String);
    email = distinctmaybe(String);
    @AccessProperty({ createUpdate: 'admin' })
    roles = one(Roles);
    locale = maybe(String);
    noTranslate = maybe(String);
    @AccessProperty({
      read: 'admin',
      update: 'admin',
      create: '',
    })
    isVerified = one(Boolean);
    @AccessProperty((o: User) => ({
      read: '',
      create: '*',
      update: or(o.id, 'admin'),
    }))
    password = maybe(String); // nobody is allowed to read, password = hash
    @AccessProperty({
      read: 'admin',
      create: '',
      update: 'admin',
    })
    failedLogins = maybe(Integer);
    @AccessProperty({
      read: 'admin',
      create: '',
      update: 'admin',
    })
    lockedUntil = maybe(Date);
    @AccessProperty((o: User) => ({
      read: or(o.id, 'admin'),
      create: '',
      update: '',
    }))
    lastLogin = maybe(Date);
    @AccessProperty((o: User) => ({
      read: or(o.id, 'admin'),
      create: '',
      update: '',
    }))
    createdAt = one(Date);
    @AccessProperty((o: User) => ({
      read: or(o.id, 'admin'),
    }))
    about = one(String);
    @AccessProperty((o: User) => ({
      read: or(o.id),
    }))
    avatar = maybe(File, (o) => o.avatarsFor);
    uploadedFiles = many(File);
    pages = many(Page, (o) => o.author);
    projects = many(Project, (o) => o.author);
    publications = many(Publication, (o) => o.author);
    member = maybe(Member, (o) => o.user);
    @AccessProperty({ read: 'admin', createUpdate: '' })
    logs = many(Log);
  }

  @Access({
    create: or('manager'),
    read: '*',
    update: or('manager'),
    delete: or('manager'),
  })
  class MenuItem extends It {
    title = one(String);
    slug = one(String);
    text = one(Json);
    menu = one(Menu);
    farey_order = one(String);
  }

  @Access({
    create: or('manager'),
    read: '*',
    update: or('manager'),
    delete: or('manager'),
  })
  class Menu extends It {
    title = one(String);
    items = many(MenuItem);
    locale = one(String);
  }

  @Access((o: Page) => ({
    create: or('manager'),
    read: '*',
    update: or(o.author, 'manager'),
    delete: or(o.author, 'manager'),
  }))
  class Page extends It {
    @AccessProperty({
      createUpdate: 'admin',
    })
    author = one(User);
    title = one(String);
    @AccessProperty({ createUpdate: 'manager' })
    slug = one(String);
    sections = many(Section);
    @AccessProperty({ createUpdate: 'manager' })
    locale = one(String);
  }

  @Access((o: Section) => ({
    create: o.page,
    read: '*',
    update: o.page,
    delete: o.page,
  }))
  class Section extends It {
    text = one(Json);
    files: Wrapped<File> = many(File, (o) => o.sectionFiles);
    page = one(Page);
    farey_order = one(String);
    contenttype = maybe(String);
    content = maybe(Integer);
  }

  @Access((o: Project) => ({
    create: or('manager'),
    read: '*',
    update: or(o.author, 'manager'),
    delete: or('manager'),
  }))
  class Project extends It {
    @AccessProperty({
      createUpdate: 'admin',
    })
    author = one(User);
    title_en = one(String);
    title_nl = one(String);
    text_en = one(String);
    text_nl = one(String);
    image = maybe(File, (o) => o.projectImageFor);
    image_contain = maybe(Boolean);
    farey_order = one(String);
    startDate = one(Date);
    endDate = maybe(Date);
    duration = one(String);
    status = one(Status);
    publications = many(Publication);
    members = many(Member);
  }

  @Access((o: Publication) => ({
    create: or('manager'),
    read: '*',
    update: or(o.author, 'manager'),
    delete: or('manager'),
  }))
  class Publication extends It {
    @AccessProperty({
      createUpdate: 'admin',
    })
    author = one(User);
    title = one(String);
    text = one(String);
    image = maybe(File, (o) => o.publicationImageFor);
    image_contain = maybe(Boolean);
    farey_order = one(String);
    publicationDate = one(Date);
    status = one(Status);
    download = maybe(String);
    project = maybe(Project);
    members = many(Member);
  }

  @Access((o: Member) => ({
    create: or('manager'),
    read: '*',
    update: or(o.user, 'manager'),
    delete: or('manager'),
  }))
  class Member extends It {
    @AccessProperty({
      createUpdate: 'admin',
    })
    user = distinctmaybe(User);
    name = one(String);
    firstname = one(String);
    text_en = one(String);
    text_nl = one(String);
    image = maybe(File, (o) => o.memberImageFor);
    image_contain = maybe(Boolean);
    farey_order = one(String);
    startDate = one(Date);
    endDate = maybe(Date);
    status = one(Status);
    function_en = maybe(String);
    function_nl = maybe(String);
    linkedin = maybe(String);
    orcid = maybe(String);
    publications = many(Publication);
    projects = many(Project);
  }

  @Access((o: Folder) => ({
    create: or('manager'),
    read: or('*', 'manager'),
    update: or('manager'),
    delete: or('manager'),
  }))
  class Folder extends It {
    folderName = one(String);
    parentFolder: Wrapped<Folder> = maybe(Folder, (o) => o.subFolders);
    subFolders: Wrapped<Folder> = many(Folder, (o) => o.parentFolder);
    files = many(File);
  }

  @Access((o: Session) => ({
    create: '*',
  }))
  class Session extends It {
    // TODO: return a meaningfull Id, or change generated OpenAP
    Email = one(String);
    @Constraint((str: String) => str.length > 5)
    Password = one(String);
  }

  @Access('admin')
  class VerificationCode extends It {
    userId = one(Integer); // No reference to User by design, just a regular integer
    hmac = one(String);
    salt = one(String);
    expiresAt = one(Date);
    attemptsLeft = one(Integer);
  }

  @Access((o: Account) => ({
    update: '*',
  }))
  class Account extends It {
    Email = one(String);
    @Constraint((str: String) => str.length > 5)
    Password = one(String);
    Token = one(String);
    RegistrationCode = one(String);
    Action = one(String); // actions like: reset password, verify token, etc.
  }

  @Access({
    createUpdate: '*',
    read: 'admin',
  })
  class Log extends It {
    message = one(String);
    timestamp = one(Date);
    @AccessProperty({ createUpdate: '' })
    user = one(User);
  }

  @Access('admin')
  class DBLog extends It {
    table_name = one(String);
    old_row_data = maybe(Json);
    new_row_data = maybe(Json);
    dml_type = one(DBLogType);
    dml_timestamp = one(Date);
  }

  @Access('admin')
  class ApplicationLog extends It {
    message = one(Json);
    timestamp = distinct(BigInteger);
  }

  @Access({
    createUpdate: 'admin',
    read: '*',
  })
  class ApplicationSettings extends It {
    tools = maybe(Json);
    rights = maybe(Json);
    maxmbs = maybe(Json);
    config = maybe(Json);
  }

  const DBLogType = ['INSERT', 'UPDATE', 'DELETE'];

  return {
    enums: { Status, Roles, DBLogType },
    rolesInheritance,
    classes: [
      ApplicationLog,
      ApplicationSettings,
      File,
      Folder,
      User,
      Session,
      Log,
      Account,
      DBLog,
      VerificationCode,
      Menu,
      MenuItem,
      Page,
      Section,
      Project,
      Publication,
      Member,
    ],
  };
};
